import { computed, DestroyRef, inject, Injectable, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CardInfo, DeviceConfig, DeviceHealth, DnieData, TauriResponse } from '../interfaces/tauri.interface';
import { HelperRoutingService } from '@common/services/helper-routing.service';
import { HelperTimeService } from '@common/services/helper-time.service';
import { HttpFichaService } from '@fichaje/services/http-ficha.service';
import { HelperService } from '@common/services/helper.service';
import { Observable } from 'rxjs/internal/Observable';
import { map } from 'rxjs/operators';
import { ProneFirstLoad } from '@fichaje/interfaces/schedule.interface';

// 1. Importa los módulos de Tauri que vayas a usar
import { invoke } from '@tauri-apps/api/core';
import { listen } from '@tauri-apps/api/event';
import { PreloadConfigService } from '@common/config/preload-config.service';
import { BgImage } from '@fichaje/interfaces/ficha.interface';
import { ToastService } from '@common/components/base/toast/toast.service';
import { environment } from '../../../environments/environment';

@Injectable({ providedIn: 'root' })
export class ProneService {

  private readonly getQrCodeInvoke = 'get_qr_code_svg';
  private readonly heartbeatInvoke = 'remote_app_heartbeat';
  private readonly disconnectInvoke = 'set_device_disconnected_and_stop_verification_loop';
  private readonly disconnectTemporalInvoke = 'set_device_disconnected';
  private readonly readDnieInvoke = 'read_dnie';
  private readonly maxTarjetaDigits = 10;
  private readonly proneW2Model = 'PRONE W2';

  private readonly proneBasePath = '/prone-operations';
  private readonly proneFirstLoadStorageKey = 'proneFirstLoadStorage';
  private readonly workerPath = ['prone-operations', 'worker'] as const;
  private readonly welcomeNumpadPath = ['prone-operations', 'welcome-numpad'] as const;
  private readonly welcomeNfcPath = ['prone-operations', 'welcome-nfc'] as const;
  private readonly dnieCanPath = ['prone-operations', 'dnie-can'] as const;

  private readonly helpRoute =            inject(HelperRoutingService);
  private readonly helpTime =             inject(HelperTimeService);
  private readonly helpData =             inject(HelperService);
  private readonly http =                 inject(HttpFichaService);
  private readonly preConfServ =          inject(PreloadConfigService);
  private readonly destroyRef =          inject(DestroyRef);
  private readonly toast =                inject(ToastService);
  // qr-server (SEP, ver docs/infra/single-entry-point.md) exige 'data' y admite
  // 'format' (webp/png/jpg/svg); usamos webp por tamaño (~400B vs varios KB en png/jpg).
  // 'data' es la misma URL que codificaba el QR nativo de Tauri (device_config.url).
  public readonly qrImgServer = computed(() => {
    const base = this.preConfServ.config()?.qrUrl;
    const data = this.deviceConfig()?.url;
    if (!base || !data) return undefined;

    return `${base}?data=${encodeURIComponent(data)}&format=webp`;
  });
  public readonly  currentLangFromRoute =   computed(() => this.helpRoute.currentLangFromRoute());

  private readonly tauriResponseSignal =  signal<TauriResponse|undefined>(undefined);
  public readonly  deviceConfig =           computed(() => this.tauriResponseSignal()?.device_config);
  public readonly  qrSVGimg =               computed(() => this.tauriResponseSignal()?.message);
  public readonly  proneToken =             computed(() => this.deviceConfig()?.token);
  public readonly  backgroundImages =     signal<BgImage[]|undefined>(undefined);
  public readonly  companyIdFromBg =      signal<number|undefined>(undefined)

  // Solo los Prone modelo "PRONE W2" tienen lector NFC compatible con DNIe
  // contactless (PACE-CAN). En el resto de modelos, `dnie_card_detected` se
  // ignora (ver handleDnieCardDetected). Ver isProneW2() en
  // fichanet-station-service.js (legacy AngularJS): mismo criterio, ahí sobre
  // `$rootScope.modelo` en vez de esta señal derivada de deviceConfig().
  public readonly isProneW2 = computed(() => this.deviceConfig()?.device_model === this.proneW2Model);

  private readonly cardDetectedSignal = signal<CardInfo | undefined>(undefined);
  public readonly cardDetected = computed(() => this.cardDetectedSignal());
  private dnieNfcListenersReady = false;

  setTauriResponse(obj:TauriResponse|undefined) {
    this.tauriResponseSignal.set(obj);
  }

  backgroundImg = 'assets/backgrounds/background.webp';
  // public textoNfcLeidoTemporal?:string;
  // backgroundImg = 'assets/backgrounds/christmas03.png';
  // backgroundImg = 'assets/backgrounds/christmas02.webp';
  // backgroundImg = 'assets/backgrounds/christmas05.jpg';
  // backgroundImg = 'assets/backgrounds/christmas06.jpg';

  public getWorkerFirstLoad(inputCode:string, inputStringDayISO:string, token:string|undefined = this.proneToken()): Observable<ProneFirstLoad> {

    console.log('Current Full URL:', this.helpRoute.currentRoute().fullUrl)
    if (!inputCode || !token) {
      console.error('ProneService - getUserLogged: token & inputCode', {token, inputCode} );
    }
    const currentLang = this.currentLangFromRoute();

    return this.http.getProneFirstLoad( inputCode, inputStringDayISO ).pipe(
      // INTERCEPTADO PARA CAMBIAR LANGUAGE DEL USERLOGED
      map((firstLoad) => {
        this.syncLanguageWithUser(firstLoad, currentLang, token);
        return firstLoad;
      })
    );
  }

  private syncLanguageWithUser(
    firstLoad: ProneFirstLoad,
    currentLang: string | undefined,
    token: string | undefined,
  ): void {
    if(!this.proneToken()) { return }
    const userlang = firstLoad.user_logged.language;
    if (!token || !currentLang || currentLang === userlang) {
      return;
    }
    // Hay diferencia entre el idioma actual y el del usuario, requiere navegar al lenguaje correcto.
    // agregar DATA a localstorage
    this.helpData.localStorage('set', this.proneFirstLoadStorageKey, firstLoad);
    // saltar al idioma del usuario
    this.helpRoute.navigateToLanguaje(userlang, token, this.proneBasePath);
  }

  async remoteAppHeartbeat(): Promise<void> {
    try {
      await invoke(this.heartbeatInvoke);
    }
    catch (error) {
      console.log(`dentro de catch de => prone.service invoke("${this.heartbeatInvoke}")`);
      console.error(`Error en invoke("${this.heartbeatInvoke}"):`, error);
    }
  }

  async loadTauriResponse(): Promise<boolean> {
    const toInvoke = this.getQrCodeInvoke;  // 'get_device_config';
    try {
      const tauriResp:TauriResponse = await invoke(toInvoke);
      this.normalizeDeviceHealth(tauriResp);
      this.setTauriResponse(tauriResp);
      // this.displayDialogs();
      const hasNavigated = this.goToLanguageLogic();

      if (!hasNavigated) {
        this.goToStartupMode();
      }

      return true
    }
    catch (error) {
      this.handleTauriLoadError(toInvoke, error);
      return false
    }
  }

  private handleTauriLoadError(toInvoke: string, error: unknown): void {
    // Maneja el error y asigna el objeto vacío en caso de fallo.
    console.log('dentro de catch de => prone invoke('+toInvoke+'), a punto de ir a sin conexion');
    this.goSinConexionAndStay('from error first load');
    console.error('Error en invoke("'+toInvoke+'"):', error);
  }

  goToLanguageLogic():boolean {
    // x) Si no hay un leguaje en el route (modo sin idioma, local debugging), no hacer nada;
    // console.log('Current Full URL:', this.helpRoute.currentRoute().fullUrl)
    const currentLangFromRoute = this.currentLangFromRoute();
    // console.log('currentLangFromRoute ', currentLangFromRoute)
    if (!currentLangFromRoute) {
      // console.log('La ruta no contiene un lenguaje, saliendo de seleccion de lenguaje')
      return false
    }

    if (this.navigateToWorkerFromStorage()) {
      return true;
    }

    return this.goToDeviceLanguage(currentLangFromRoute, this.deviceConfig())
  }

  setBackgrounds(){
    const token = this.proneToken();
    if (!token) return;
    this.http.getBackgroundImages(token, true)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (backImgs) => {
          this.backgroundImages.set(backImgs.data);
          this.companyIdFromBg.set(backImgs.meta.company_id)
        },
        error: (error: unknown) => {
          console.error('backgroundImages Error:',error);
        }
      })

  };

  private navigateToWorkerFromStorage(): boolean {
    const proneFirstLoadInProcess = this.helpData.localStorage('get', this.proneFirstLoadStorageKey) as ProneFirstLoad | undefined;
    if (!proneFirstLoadInProcess?.user_logged?.id) {
      return false;
    }

    // ir a fichajePage en lugar de welcomePage.
    const inputStringDayISO = this.helpTime.dateToLocalTimePretty(new Date(), 'yyyy-mm-dd')!;
    this.helpRoute.navigateFullPath([...this.workerPath, '22222222', inputStringDayISO])
    return true;
  }

  private normalizeDeviceHealth(tauriResp: TauriResponse): void {
    if (!tauriResp.device_health) {
      return;
    }

    if (typeof tauriResp.device_health === 'string') {
      const healthParsed: DeviceHealth = JSON.parse(tauriResp.device_health);
      tauriResp.device_health = healthParsed;
    }
  }

  goToDeviceLanguage(
    currentLangFromRoute:string|undefined = this.currentLangFromRoute(),
    deviceConf:DeviceConfig|undefined = this.deviceConfig()
  ):boolean {
    if (!deviceConf || !currentLangFromRoute) {
      return false
    }
    const {token, default_device_language} = deviceConf

    if (default_device_language === currentLangFromRoute) {
      return false;
    }

    this.helpRoute.navigateToLanguaje(default_device_language, token, this.proneBasePath)
    return true;
  }


  goToStartupMode(){
    const mode = this.deviceConfig()?.startup_mode
    if (mode === 'code') {
      this.helpRoute.navigateFullPath([...this.welcomeNumpadPath])
    } else {
      this.helpRoute.navigateFullPath([...this.welcomeNfcPath])
    }
  }
  // ──────────────────────────────────────────────────────────────────────
  // Lectura de DNIe (NFC contactless + CAN, y DNIe físico por contacto)
  // ──────────────────────────────────────────────────────────────────────
  // Traducción a signals del flujo de fichanet-station-service.js /
  // proneModalTecladoCanDnie.js / inicio-station-app.js (app legacy
  // AngularJS): el hilo lector NFC de Tauri emite estos eventos
  // independientemente de la pantalla activa, así que se registran una
  // única vez (el servicio es un singleton `providedIn: 'root'`), no por
  // página — igual que hace fichanetStationService en el legacy.
  // ──────────────────────────────────────────────────────────────────────

  initDnieNfcListeners(): void {
    if (this.dnieNfcListenersReady) return;
    this.dnieNfcListenersReady = true;

    listen<CardInfo>('dnie_card_detected', (event) => {
      this.cardDetectedSignal.set(event.payload);
      this.handleDnieCardDetected(event.payload);
    }).catch((error) => console.error('Error en listen("dnie_card_detected"):', error));

    listen('dnie_card_removed', () => {
      this.cardDetectedSignal.set(undefined);
    }).catch((error) => console.error('Error en listen("dnie_card_removed"):', error));

    listen<{ uid: string }>('mifare_uid_detected', (event) => {
      this.handleMifareUidDetected(event.payload.uid);
    }).catch((error) => console.error('Error en listen("mifare_uid_detected"):', error));

    listen('mifare_read_error', () => {
      this.notifyReadError();
    }).catch((error) => console.error('Error en listen("mifare_read_error"):', error));

    // Vía de contacto: DNIe físico insertado y leído por pkcs15-tool (sin
    // CAN, sin pasar por dnie-can). Emite el DNI de 8 dígitos sin letra —
    // mismo formato que un `codigo` manual, así que se identifica igual.
    listen<{ dni: string }>('dni_detected', (event) => {
      this.handleDniDetected(event.payload.dni);
    }).catch((error) => console.error('Error en listen("dni_detected"):', error));

    listen('dni_read_error', () => {
      this.notifyReadError();
    }).catch((error) => console.error('Error en listen("dni_read_error"):', error));
  }

  private handleDnieCardDetected(card: CardInfo): void {
    if (card.card_type !== 'DnieContactless' || !this.isProneW2() || !this.canProcessCardEvent()) {
      return;
    }
    this.helpRoute.navigateFullPath([...this.dnieCanPath]);
  }

  private handleMifareUidDetected(uid: string): void {
    if (!this.canProcessCardEvent()) {
      return;
    }
    this.onTextEmited(this.padTarjeta(uid));
  }

  private handleDniDetected(dni: string): void {
    if (!this.canProcessCardEvent()) {
      return;
    }
    this.identifyByDni(dni);
  }

  // Evita reaccionar a lecturas NFC/MIFARE mientras ya hay una sesión de
  // fichaje abierta o mientras el usuario ya está en la pantalla del CAN.
  private canProcessCardEvent(): boolean {
    const relativeRoute = this.helpRoute.currentRoute().relativeRoute;
    return !relativeRoute.includes('worker') && !relativeRoute.includes('dnie-can');
  }

  // Igual que `padTarjeta` en fichanetStationService: el UID decimal de
  // MIFARE puede tener menos de 10 dígitos, y `tarjeta` siempre se compara
  // como string de exactamente `maxTarjetaDigits` caracteres.
  private padTarjeta(uid: string): string {
    return ('0'.repeat(this.maxTarjetaDigits) + uid).slice(-this.maxTarjetaDigits);
  }

  // Identifica al trabajador por su DNI (con o sin letra) fuera del circuito
  // de tarjeta/código de onTextEmited(): un DNI no es una tarjeta NFC ni un
  // código de empresa, así que no tiene sentido pasarlo por isCardValid() ni
  // por las listas de tarjetas admin — se navega directo a la ficha.
  //
  // navigateToWorker() acaba enviando este valor a getProneFirstLoad(), que
  // lo manda como `dni` (no `codigo`) cuando tiene 9 caracteres con letra —
  // mismo parámetro dedicado que valida
  // CalendarController::getEventsByWorkerByDateWithUserLogged en el backend
  // (separado de `codigo`, que solo acepta 8 dígitos numéricos).
  identifyByDni(dni: string): void {
    this.navigateToWorker(dni);
  }

  // Lee el DNIe presente en el lector NFC mediante PACE-CAN.
  readDnieNfc(can: string): Promise<DnieData> {
    return invoke(this.readDnieInvoke, { can });
  }

  private notifyReadError(): void {
    this.toast.error(
      $localize`:@@proneService.dnieReadErrorTitle:Error`,
      $localize`:@@proneService.dnieReadErrorMessage:No ha sido posible realizar la lectura`
    );
  }

  isCardValid(text: string): boolean {
    return /^(\d{8}|\d{10})$/.test(text);
  }
  isCardAdmin(text: string, adminCards: string[]):boolean {
    // if the provided text is present in the adminCards list, goes "sin conexion"
    const isAdmin = adminCards.includes(text);
    if(isAdmin) {
      this.goSinConexionAndStay(text);
      return true
    }
    return false
  }
  onTextEmited(t:string):string {
    console.log('ProneService - onTextEmited - t:', t);
    const config = this.deviceConfig()
    if (!config) {
      return 'noDeviceConfig';
    }

    return this.processCardText(t, config);
  }

  private processCardText(text: string, config: DeviceConfig): string {
    if (!this.isCardValid(text)) {
      //! Aqui va el evento en caso no error de validacion
      return 'error: ' + text;
    }

    const adminCards = this.getAdminCards(config);
    const isAdmin = this.isCardAdmin(text, adminCards);
    if (!isAdmin) {
      this.navigateToWorker(text);
    }

    return 'valid';
  }

  private getAdminCards(config: DeviceConfig): string[] {
    return config.nsc_admin_card_list.concat(config.client_admin_card_list);
  }

  private navigateToWorker(inputCode: string): void {
    this.helpRoute.navigateFullPath([
      ...this.workerPath,
      inputCode,
      this.helpTime.dateToStringDayISO(new Date())
    ]);
  }

  async goSinConexionAndStay(text:string): Promise<void> {
    try {
      // Uso de await en lugar de .then() encadenado.
      const resp = await invoke(this.disconnectInvoke, { card: text});
      // Asigna el resultado a la propiedad de la clase (`this.health`).
      if(!resp) globalThis.alert('TESTING: Deberia aparecer solo en el PRONE y esta ventana nunca deberia mostrarse, si lo hace es un error que requiere reportarse a sistemas.');

    } catch (error) {
      // Si no consigue pasar a sin conexion...;
      this.navigateToLogin();
      console.error(`Error en invoke("${this.disconnectInvoke}"):`, error);
    }
  }
  navigateToLogin(){
    // '/fichaje' es la ruta de la app web (app.routes.ts); en el kiosk
    // (app.routes.kiosk.ts) no existe, y cae en el fallback genérico
    // ':rutaRecibida' (error-layout) en vez de recuperar el flujo del kiosk.
    this.helpRoute.navigateFullPath([environment.isKiosk ? this.proneBasePath : '/fichaje'])
  }

  async goSinConexionAndComeback(){
    try {
      // Uso de await en lugar de .then() encadenado.
      const resp = await invoke(this.disconnectTemporalInvoke);
      // Asigna el resultado a la propiedad de la clase (`this.health`).
      if(!resp) console.error('tauri no responde')

    } catch (error) {
      // Si no consigue pasar a sin conexion...;
      this.navigateToLogin();
      console.error(`Error en invoke("${this.disconnectTemporalInvoke}"):`, error);
    }
  }

  /**
   * Muestra modal prone con mensaje traducido del backend.
   * TODO: integrar con ProneQuickDialogsFactoryService + LogicChain event-bus.
   * Por ahora solo log — la integración completa requiere coordinación con el
   * sistema de dialogs del prone-fichaje-page.component.
   */
  showLogoutModal(options: { message: string; color: string; dni: string | null; name: string | null; hora: string | null }): void {
    console.warn('[ProneService.showLogoutModal] TODO: integrate with ProneQuickDialogsFactoryService', options);
  }
}
